import React, { useState, useEffect } from 'react';
import { Save, Info, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { getTaxSettings, updateTaxSettings } from '../../api/taxSettings';

const TaxSettingsPage = () => {
  // Mock data - in real app, fetch from backend API if dynamic
  const [bands, setBands] = useState([
    { min: 0, max: 300000, rate: 7 },
    { min: 300000, max: 600000, rate: 11 },
    { min: 600000, max: 1100000, rate: 15 },
    { min: 1100000, max: 1600000, rate: 19 },
    { min: 1600000, max: 3200000, rate: 21 },
    { min: 3200000, max: 'Above', rate: 24 }
  ]);

  const [settings, setSettings] = useState({
    vat_enabled: true,
    vat_rate: 7.5,
    vat_pricing_mode: 'exclusive',
    nhf_rate: 2.5,
    pension_employee_rate: 8,
    pension_employer_rate: 10,
    paye_enabled: true,
    nhf_enabled: true,
    pension_employee_enabled: true,
    pension_employer_enabled: true
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await getTaxSettings();
        if (response.success && response.data) {
          setSettings({
            vat_enabled: response.data.vat_enabled ?? true,
            vat_rate: parseFloat(response.data.vat_rate) || 7.5,
            vat_pricing_mode: response.data.vat_pricing_mode || 'exclusive',
            nhf_rate: parseFloat(response.data.nhf_rate) || 2.5,
            pension_employee_rate: parseFloat(response.data.pension_employee_rate) || 8,
            pension_employer_rate: parseFloat(response.data.pension_employer_rate) || 10,
            paye_enabled: response.data.paye_enabled ?? true,
            nhf_enabled: response.data.nhf_enabled ?? true,
            pension_employee_enabled: response.data.pension_employee_enabled ?? true,
            pension_employer_enabled: response.data.pension_employer_enabled ?? true
          });
        }
      } catch (error) {
        console.error('Error fetching tax settings:', error);
        toast.error('Failed to load tax settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await updateTaxSettings(settings);
      if (response.success) {
        toast.success('Tax settings saved successfully');
      } else {
        toast.error(response.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving tax settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save tax settings');
    } finally {
      setSaving(false);
    }
  };

  const handleVatToggle = () => {
    setSettings(prev => ({ ...prev, vat_enabled: !prev.vat_enabled }));
  };

  const handlePayeToggle = () => {
    setSettings(prev => ({ ...prev, paye_enabled: !prev.paye_enabled }));
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Tax Configuration</h1>
          <p className="text-gray-500">Manage tax rates and statutory deductions</p>
        </div>
        <button 
          onClick={handleSave} 
          disabled={saving || loading}
          className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="grid gap-6">
        {/* VAT Settings */}
        <div className="bg-white rounded shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">VAT Settings</h2>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${settings.vat_enabled ? 'text-green-600' : 'text-gray-500'}`}>
                {settings.vat_enabled ? 'Enabled' : 'Disabled'}
              </span>
              <button
                type="button"
                onClick={handleVatToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.vat_enabled ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.vat_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${!settings.vat_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div>
              <label className="block text-sm font-medium mb-1">VAT Rate (%)</label>
              <input 
                type="number" 
                value={settings.vat_rate} 
                onChange={e => setSettings({...settings, vat_rate: parseFloat(e.target.value) || 0})} 
                className="w-full border p-2 rounded focus:ring-2 focus:ring-accent-gold focus:border-transparent" 
                step="0.1"
                min="0"
                max="100"
                disabled={!settings.vat_enabled}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">VAT Pricing Mode</label>
              <select
                value={settings.vat_pricing_mode}
                onChange={e => setSettings({...settings, vat_pricing_mode: e.target.value})}
                className="w-full border p-2 rounded focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                disabled={!settings.vat_enabled}
              >
                <option value="exclusive">VAT Exclusive (Added on top)</option>
                <option value="inclusive">VAT Inclusive (Already in price)</option>
              </select>
            </div>
          </div>

          {/* Info Box */}
          <div className={`mt-4 p-3 rounded-lg border ${settings.vat_enabled ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-start gap-2">
              <Info className={`w-5 h-5 mt-0.5 flex-shrink-0 ${settings.vat_enabled ? 'text-blue-600' : 'text-gray-400'}`} />
              <div className="text-sm">
                {settings.vat_enabled ? (
                  settings.vat_pricing_mode === 'exclusive' ? (
                    <p className="text-blue-800">
                      <strong>VAT Exclusive:</strong> VAT will be calculated and added on top of product prices at checkout. 
                      Example: ₦10,000 product + ₦750 VAT = ₦10,750 total.
                    </p>
                  ) : (
                    <p className="text-blue-800">
                      <strong>VAT Inclusive:</strong> VAT is already included in product prices. The VAT amount will be extracted and shown separately on receipts.
                      Example: ₦10,000 product (includes ₦698 VAT).
                    </p>
                  )
                ) : (
                  <p className="text-gray-600">
                    VAT is currently disabled. No tax will be applied to sales transactions.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Other Statutory Rates */}
        <div className="bg-white rounded shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Other Statutory Rates (%)</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* NHF */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium">NHF Rate</label>
                <button
                  type="button"
                  onClick={() => setSettings(prev => ({ ...prev, nhf_enabled: !prev.nhf_enabled }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    settings.nhf_enabled ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    settings.nhf_enabled ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              <input 
                type="number" 
                value={settings.nhf_rate} 
                onChange={e => setSettings({...settings, nhf_rate: parseFloat(e.target.value) || 0})} 
                className="w-full border p-2 rounded focus:ring-2 focus:ring-accent-gold disabled:opacity-50" 
                step="0.1" 
                disabled={!settings.nhf_enabled}
              />
            </div>

            {/* Pension Employee */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium">Pension (Employee)</label>
                <button
                  type="button"
                  onClick={() => setSettings(prev => ({ ...prev, pension_employee_enabled: !prev.pension_employee_enabled }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    settings.pension_employee_enabled ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    settings.pension_employee_enabled ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              <input 
                type="number" 
                value={settings.pension_employee_rate} 
                onChange={e => setSettings({...settings, pension_employee_rate: parseFloat(e.target.value) || 0})} 
                className="w-full border p-2 rounded focus:ring-2 focus:ring-accent-gold disabled:opacity-50" 
                step="0.1" 
                disabled={!settings.pension_employee_enabled}
              />
            </div>

            {/* Pension Employer */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium">Pension (Employer)</label>
                <button
                  type="button"
                  onClick={() => setSettings(prev => ({ ...prev, pension_employer_enabled: !prev.pension_employer_enabled }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    settings.pension_employer_enabled ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    settings.pension_employer_enabled ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              <input 
                type="number" 
                value={settings.pension_employer_rate} 
                onChange={e => setSettings({...settings, pension_employer_rate: parseFloat(e.target.value) || 0})} 
                className="w-full border p-2 rounded focus:ring-2 focus:ring-accent-gold disabled:opacity-50" 
                step="0.1" 
                disabled={!settings.pension_employer_enabled}
              />
            </div>
          </div>
        </div>

        {/* PAYE Bands */}
        <div className="bg-white rounded shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">PAYE Tax Bands (Annual)</h2>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${settings.paye_enabled ? 'text-green-600' : 'text-gray-500'}`}>
                {settings.paye_enabled ? 'Enabled' : 'Disabled'}
              </span>
              <button
                type="button"
                onClick={handlePayeToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.paye_enabled ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.paye_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          <div className={!settings.paye_enabled ? 'opacity-50 pointer-events-none' : ''}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Band</th>
                <th className="px-4 py-2 text-left">Income Range (₦)</th>
                <th className="px-4 py-2 text-right">Rate (%)</th>
              </tr>
            </thead>
            <tbody>
              {bands.map((band, i) => (
                <tr key={i} className="border-t">
                  <td className="px-4 py-2">{i + 1}</td>
                  <td className="px-4 py-2">
                    {band.max === 'Above' 
                      ? `Above ${new Intl.NumberFormat().format(band.min)}`
                      : `${new Intl.NumberFormat().format(band.min)} - ${new Intl.NumberFormat().format(band.max)}`
                    }
                  </td>
                  <td className="px-4 py-2 text-right">{band.rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-500 mt-2">
            * Note: Tax bands are currently static based on Nigeria Personal Income Tax Act 2024.
          </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxSettingsPage;

